import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUsers, mediaUrl, type AdminUser } from '../api';
import { badgeClass, fmtDate } from '../format';
import { useApi } from '../useApi';
import { Pager } from '../components/Pager';

const PAGE_SIZE = 50;

type SortKey = 'name' | 'gender' | 'status' | 'verified' | 'lastActive' | 'created';

function initial(u: AdminUser): string {
  return (u.displayName ?? u.username ?? '?').trim().charAt(0).toUpperCase() || '?';
}

export function Users() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  // column filters
  const [gender, setGender] = useState('');
  const [status, setStatus] = useState('');
  const [verified, setVerified] = useState<'' | 'true' | 'false'>('');
  // sorting
  const [sort, setSort] = useState<SortKey>('created');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Any filter/sort/search change goes back to the first page.
  useEffect(() => {
    setPage(1);
  }, [query, gender, status, verified, sort, order]);

  const { data, error, loading } = useApi(
    () => getUsers({ search: query || undefined, page, pageSize: PAGE_SIZE, gender: gender || undefined, status: status || undefined, verified: verified || undefined, sort, order }),
    [query, page, gender, status, verified, sort, order],
  );
  const rows: AdminUser[] = data?.data ?? [];

  // Click a sortable header: same column → flip direction; new column → sensible default.
  const toggleSort = (key: SortKey) => {
    if (sort === key) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      setOrder(key === 'name' || key === 'gender' ? 'asc' : 'desc');
    }
  };
  const arrow = (key: SortKey) => (sort === key ? (order === 'asc' ? ' ▲' : ' ▼') : '');
  const sortable = (key: SortKey, label: string) => (
    <th className="th-sort" onClick={() => toggleSort(key)} title="Sort">
      {label}
      <span className="th-arrow">{arrow(key)}</span>
    </th>
  );

  return (
    <div className="admin-wrap">
      <div className="page-head">
        <h1>
          Users<span className="dot">.</span>
        </h1>
        <input
          className="input search"
          type="search"
          placeholder="Search name or @username"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* column filters */}
      <div className="filters-bar">
        <select className="input" value={gender} onChange={(e) => setGender(e.target.value)} aria-label="Filter by gender">
          <option value="">All genders</option>
          <option value="man">Men</option>
          <option value="woman">Women</option>
          <option value="nonbinary">Non-binary</option>
        </select>
        <select className="input" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="banned">Banned</option>
        </select>
        <select className="input" value={verified} onChange={(e) => setVerified(e.target.value as '' | 'true' | 'false')} aria-label="Filter by verified">
          <option value="">Any verification</option>
          <option value="true">Verified</option>
          <option value="false">Not verified</option>
        </select>
        {(gender || status || verified) && (
          <button className="btn" onClick={() => { setGender(''); setStatus(''); setVerified(''); }}>
            Clear
          </button>
        )}
        {data && <span className="muted" style={{ marginLeft: 'auto' }}>{data.total} total</span>}
      </div>

      <div className="table-wrap">
        {error ? (
          <div className="empty">{error}</div>
        ) : loading && !data ? (
          <div className="empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="empty">No users found.</div>
        ) : (
          <table className="flm">
            <thead>
              <tr>
                {sortable('name', 'User')}
                <th>City</th>
                {sortable('gender', 'Gender')}
                <th>Plan</th>
                {sortable('status', 'Status')}
                {sortable('verified', 'Verified')}
                {sortable('lastActive', 'Last active')}
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} className="clickable" onClick={() => navigate('/users/' + u.id)}>
                  <td>
                    <div className="user-cell">
                      {u.photoUrl ? (
                        <img className="avatar" src={mediaUrl(u.photoUrl)} alt="" referrerPolicy="no-referrer" />
                      ) : (
                        <span className="avatar">{initial(u)}</span>
                      )}
                      <div>
                        <div className="uc-name">{u.displayName || '—'}</div>
                        <div className="uc-sub">@{u.username}</div>
                      </div>
                    </div>
                  </td>
                  <td>{u.city ?? '—'}</td>
                  <td className="muted">{u.gender ?? '—'}</td>
                  <td>
                    <span className={`plan-badge ${u.effectivePlanId}`}>{u.effectivePlanId}</span>
                  </td>
                  <td>
                    <span className={badgeClass(u.status)}>{u.status}</span>
                  </td>
                  <td className="muted">{u.verified ? 'Yes' : '—'}</td>
                  <td className="muted">{fmtDate(u.lastActiveAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data && (
        <Pager page={data.page ?? page} pageSize={data.pageSize ?? PAGE_SIZE} total={data.total} loading={loading} onPage={setPage} />
      )}
    </div>
  );
}
